import { AgelessAssessmentFlowType } from "@/core/api/orval/model/agelessAssessmentFlowType";
import type { DataPrivacyRegion } from "@/core/api/orval/model/dataPrivacyRegion";
import { DataPrivacyRegion as DataPrivacyRegionConst } from "@/core/api/orval/model/dataPrivacyRegion";
import type { Dictionary } from "@/core/i18n/__generated__/dictionary";
import { formatPhoneForDisplay } from "@/core/utils/phone.utils";

/** Face-scan checkbox copy: Canada requires minimum age 19; other regions use 18. */
export function getBiometricConsentCheckboxPrefixKey(region: DataPrivacyRegion): keyof Dictionary {
  return region === DataPrivacyRegionConst.Canada
    ? "assessmentSteps.studioSession.consent.checkboxPrefixCanada"
    : "assessmentSteps.studioSession.consent.checkboxPrefix";
}

/**
 * Records the UI locale the user was viewing when they consented.
 * It does not select which consent copy is shown (that is driven by DataPrivacyRegion).
 */
export function deriveConsentLanguageCode(uiLang: string): string {
  return uiLang;
}

const AESTHETICS_CAPTURE_CONSENT_TEXT_VERSION = "v2.0";
const WELLNESS_CAPTURE_CONSENT_TEXT_VERSION = "v1.0";

export function getAssessmentCaptureConsentTextVersion(
  flowType: AgelessAssessmentFlowType,
): string {
  return flowType === AgelessAssessmentFlowType.Wellness
    ? WELLNESS_CAPTURE_CONSENT_TEXT_VERSION
    : AESTHETICS_CAPTURE_CONSENT_TEXT_VERSION;
}

export const CONTACT_SMS_CONSENT_TEXT_VERSION_US = "ageless-contact-sms-v1";
export const CONTACT_TRANSACTIONAL_CONSENT_TEXT_VERSION_US = "ageless-transactional-v1";
/** The wording of Canada's one combined checkbox when it cannot name the practice (`contactConsentCanadaCombined`). */
export const CONTACT_COMBINED_CONSENT_TEXT_VERSION_CA = "ageless-contact-combined-ca-v1";
/**
 * The wording of Canada's one combined checkbox naming the practice, its mailing address and its phone number
 * (`contactConsentCanadaCombinedForPractice`). The server counts a consent given under exactly this id as express
 * consent to the practice's promotional emails, so bump it together with
 * `AgelessContactConsentTextVersions.ContactCombinedPracticeConsentTextVersionCa` whenever the copy changes.
 */
export const CONTACT_COMBINED_PRACTICE_CONSENT_TEXT_VERSION_CA = "ageless-contact-combined-ca-v3";

/** The practice Canada's combined checkbox names: who sends, where to write to it, and how to call it. */
export type ConsentPracticeDetails = {
  readonly name: string;
  readonly mailingAddress: string;
  readonly phoneNumber: string;
};

type ConsentPracticeLocation = {
  readonly phoneNumber?: string | null;
  readonly addressLine1?: string | null;
  readonly addressLine2?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly postalOrZipCode?: string | null;
};

/**
 * The practice details Canada's combined checkbox names, taken from the practice's first location with both a full
 * mailing address and a phone number. Null while the practice is unknown or when no location has both, in which case
 * the checkbox keeps its wording that names no practice and does not count as consent to promotional email.
 */
export function selectConsentPracticeDetails(
  practiceName: string | null,
  locations: readonly ConsentPracticeLocation[],
): ConsentPracticeDetails | null {
  const name = practiceName?.trim();
  if (!name) {
    return null;
  }

  for (const location of locations) {
    const street = location.addressLine1?.trim();
    const city = location.city?.trim();
    const province = location.state?.trim();
    const postalCode = location.postalOrZipCode?.trim();
    const phoneNumber = location.phoneNumber?.trim();
    if (street && city && province && postalCode && phoneNumber) {
      const unit = location.addressLine2?.trim();
      return {
        name,
        mailingAddress: [street, unit, city, `${province} ${postalCode}`]
          .filter(Boolean)
          .join(", "),
        phoneNumber: formatPhoneForDisplay(phoneNumber),
      };
    }
  }

  return null;
}

/**
 * Whether the region's contact consent checkboxes can be shown yet. Canada's combined checkbox names the practice, so
 * it waits until the practice's locations have loaded: its wording, and the version recorded with it, then cannot
 * change after a patient ticks it.
 */
export function isContactConsentReady(
  region: DataPrivacyRegion,
  isPracticeLoaded: boolean,
): boolean {
  return region !== DataPrivacyRegionConst.Canada || isPracticeLoaded;
}

function getCanadaCombinedConsentTextVersion(practice: ConsentPracticeDetails | null): string {
  return practice
    ? CONTACT_COMBINED_PRACTICE_CONSENT_TEXT_VERSION_CA
    : CONTACT_COMBINED_CONSENT_TEXT_VERSION_CA;
}

export function getSmsConsentTextVersion(
  region: DataPrivacyRegion,
  practice: ConsentPracticeDetails | null,
): string {
  return region === DataPrivacyRegionConst.Canada
    ? getCanadaCombinedConsentTextVersion(practice)
    : CONTACT_SMS_CONSENT_TEXT_VERSION_US;
}

export function getTransactionalConsentTextVersion(
  region: DataPrivacyRegion,
  practice: ConsentPracticeDetails | null,
): string {
  return region === DataPrivacyRegionConst.Canada
    ? getCanadaCombinedConsentTextVersion(practice)
    : CONTACT_TRANSACTIONAL_CONSENT_TEXT_VERSION_US;
}

export type ContactConsentFieldType = "Sms" | "Transactional";

export type ContactConsentFieldDescriptor = {
  type: ContactConsentFieldType;
  required: boolean;
  labelKey: keyof Dictionary;
  labelValues?: Record<string, string>;
};

function getCanadaCombinedConsentField(
  practice: ConsentPracticeDetails | null,
): ContactConsentFieldDescriptor & { type: "Sms" } {
  if (!practice) {
    return {
      type: "Sms",
      required: false,
      labelKey: "assessmentSteps.login.contactConsentCanadaCombined",
    };
  }

  return {
    type: "Sms",
    required: false,
    labelKey: "assessmentSteps.login.contactConsentCanadaCombinedForPractice",
    labelValues: {
      practiceName: practice.name,
      mailingAddress: practice.mailingAddress,
      phoneNumber: practice.phoneNumber,
    },
  };
}

export function getContactConsentFieldDescriptors(
  region: DataPrivacyRegion,
  practice: ConsentPracticeDetails | null,
): ContactConsentFieldDescriptor[] {
  if (region === DataPrivacyRegionConst.Canada) {
    return [getCanadaCombinedConsentField(practice)];
  }

  return [
    {
      type: "Sms",
      required: true,
      labelKey: "assessmentSteps.login.smsConsent",
    },
    {
      type: "Transactional",
      required: true,
      labelKey: "assessmentSteps.login.transactionalConsent",
    },
  ];
}

export type InactiveInterestConsentFieldDescriptor = ContactConsentFieldDescriptor & {
  type: "Sms";
};

export function getInactiveInterestConsentFieldDescriptors(
  region: DataPrivacyRegion,
  practice: ConsentPracticeDetails | null,
): InactiveInterestConsentFieldDescriptor[] {
  if (region === DataPrivacyRegionConst.Canada) {
    return [getCanadaCombinedConsentField(practice)];
  }

  return [
    {
      type: "Sms",
      required: true,
      labelKey: "assessmentSteps.notActivated.smsConsent",
    },
  ];
}

/** Maps UI checkbox state to persisted SMS + transactional flags (Canada uses one combined box). */
export function resolveSmsAndTransactionalForContactSubmit(
  region: DataPrivacyRegion,
  consentChecks: Record<string, boolean>,
): { sms: boolean; transactional: boolean } {
  if (region === DataPrivacyRegionConst.Canada) {
    const combined = consentChecks.Sms ?? false;
    return { sms: combined, transactional: combined };
  }

  return {
    sms: consentChecks.Sms ?? false,
    transactional: consentChecks.Transactional ?? false,
  };
}

type BiometricOverlayItem = { type: string; label: string; required: boolean };

export function getBiometricConsentItemsForRegion(
  region: DataPrivacyRegion,
  t: (key: keyof Dictionary) => string,
): BiometricOverlayItem[] | undefined {
  return undefined;
}
